import React, { cloneElement, useEffect, useMemo, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { FolderClosedIcon, X } from "lucide-react";
import { cn } from '@/lib/utils';
import { TextInput } from '../common/text-input';
import { TextButton } from '../common/text-button';
import { IconButton } from '../common/icon-button';
import { AddFolderIcon, FolderSparkleIcon, TagIcon } from './assets';
import { Tooltip } from '../ui/modal';
import { assetsTagsHook } from '@/manager';
import { TagItem } from './tag-item';
import { ModalTagCreate } from './modal-tag-create';
import { Skeleton } from '../ui/skeleton';
import { LoadingOverlay } from '../common/loading-overlay';
import { ModalFolderCreate } from '../common/modal-folder-create';

interface ModalTagMoveProps {
  trigger?: React.ReactElement;
  open?: boolean;
  title?: string;
  createFolderLoading?: boolean;
  createTagLoading?: boolean;
  saveLoading?: boolean;
  moveButtonText?: string;
  moveButtonClassName?: string;
  moveButtonDisabled?: boolean;
  copyButtonText?: string;
  copyButtonClassName?: string;
  onCreateFolder?: (name: string) => Promise<void>;
  onCreateTag?: (name: string, folderId?: number) => Promise<void>;
  onMove?: (data: { newFolderId: number; newTagIds: number[]; }[]) => Promise<void>;
  onCopy?: (data: { newFolderId: number; newTagIds: number[]; }[]) => Promise<void>;
  onOpenChange?: (open: boolean) => void;
}

/** 标签移动弹窗 */
export const ModalTagMove: React.FC<ModalTagMoveProps> = ({
  trigger,
  open: externalOpen,
  title,
  createFolderLoading,
  createTagLoading,
  saveLoading,
  moveButtonText,
  moveButtonClassName,
  moveButtonDisabled,
  copyButtonText,
  copyButtonClassName,
  onCreateFolder,
  onCreateTag,
  onMove,
  onCopy,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);

  const [openFolderCreate, setOpenFolderCreate] = useState(false);

  const { loading, folderGroupTags, fetchAllTags } = assetsTagsHook.useFolderGroupTagData();

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
  };

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  const handleCreateFolder = async (name: string) => {
    await onCreateFolder?.(name);
    setOpenFolderCreate(false);
  }

  // 左侧导航
  const tabs = useMemo(() => [
    {
      id: 1,
      label: t('assets.material_item.move_modal.all'),
      value: folderGroupTags.flatMap(folder => folder.tags.map(tag => tag.id)).length,
    },
    {
      id: 2,
      label: t('assets.material_item.move_modal.selected'),
      value: selectedIds.length,
    },
  ], [selectedIds, folderGroupTags])

  // 创建标签
  const handleCreateTag = async (name: string, folderId?: number) => {
    try {
      await onCreateTag?.(name, folderId);
      fetchAllTags();
    } catch (error) {
      console.error("Failed to create tag:", error);
      throw error;
    }
  }

  // 选择标签
  const handleSelectTag = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  }

  // 选中的标签
  const selectedFolderGroupTags = useMemo(() => {
    return folderGroupTags
      .map(folder => ({
        ...folder,
        // 只保留被选中的 tags
        tags: folder.tags.filter(tag => selectedIds.includes(tag.id))
      }))
      // 只保留含有选中标签的文件夹
      .filter(folder => folder.tags.length > 0);
  }, [folderGroupTags, selectedIds]);

  // 格式化数据
  const getFormattedSelectedData = () => {
    // 遍历所有的文件夹分组
    const result = folderGroupTags.map(folder => {
      // 找出当前文件夹下所有被选中的 tag ID
      const selectedTagsInFolder = folder.tags
        .filter(tag => selectedIds.includes(tag.id))
        .map(tag => tag.id);

      // 返回指定要求的结构
      return {
        newFolderId: folder.id,
        newTagIds: selectedTagsInFolder
      };
    })
      // 过滤掉那些没有选中任何标签的文件夹（避免发送 new_tag_ids 为空的数据）
      .filter(item => item.newTagIds.length > 0);

    return result;
  };

  // 复制
  const handleCopy = async () => {
    const formattedData = getFormattedSelectedData();
    if (formattedData.length === 0) return;
    await onCopy?.(formattedData);
    handleClose();
  }

  const handleMove = async () => {
    const formattedData = getFormattedSelectedData();
    const isDisabled = moveButtonDisabled !== undefined ? moveButtonDisabled : formattedData.length === 0;
    if (isDisabled) return;
    await onMove?.(formattedData);
    handleClose();
  }

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay
        className={cn("fixed inset-0 bg-black/50 z-50")}
      />
      <Dialog.Content
        modal-tag-move
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[720px] max-h-[544px] min-h-[544px] flex-shrink-0",
          "z-[1001] overflow-hidden"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-6 pb-4 border-b border-[rgba(234,236,240,1)]">
          <Dialog.Title
            className='focus:outline-none text-[rgba(16,24,40,1)] text-base font-semibold flex items-center'
          >
            <span>{title || t('assets.material_item.move_modal.title')}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <IconButton
              icon={<X className="size-6 text-[rgba(71,84,103,1)]" />}
              onClick={handleClose}
            />
          </Dialog.Close>
        </div>
        <div className='flex-1 flex select-none min-h-0'>
          {/* 左侧导航 */}
          <div className='relative w-full flex flex-1'>
            <div className='flex flex-col gap-1 max-w-[190px] min-w-[190px] p-1.5 border-r border-[rgba(234,236,240,1)]'>
              {tabs.map((tab) => (
                <div
                  key={tab.id}
                  className={cn(
                    'w-full flex items-center justify-between rounded-[6px] text-sm px-3 py-2 cursor-pointer transition-all',
                    activeTab === tab.id ? 'bg-[rgba(16,24,40,0.05)]' : 'hover:bg-[rgba(16,24,40,0.02)]'
                  )}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span className='text-[rgba(16,24,40,1)]'>{tab.label}</span>
                  <span className='text-[rgba(102,112,133,1)]'>{tab.value}</span>
                </div>
              ))}
            </div>
            <Tooltip
              side='top'
              align='center'
              trigger={
                <IconButton
                  icon={<AddFolderIcon className="size-5" />}
                  className='absolute left-4 bottom-4 size-7'
                  onClick={() => setOpenFolderCreate(true)}
                />
              }
              content={
                <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
                  <span className='text-[rgba(255,255,255,1)] text-sm'>
                    {t("assets.material_item.move_modal.create_folder")}
                  </span>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
                </div>
              }
            />
          </div>
          {/* 右侧内容区域 */}
          <div className='w-full flex flex-col'>
            <div className='flex flex-1 flex-col gap-4 overflow-y-auto p-4 '>
              {(activeTab === 1 ? folderGroupTags : selectedFolderGroupTags)?.map((item) => (
                <div key={item.id} className='flex flex-col gap-3'>
                  <div className='text-[rgba(16,24,40,1)] text-sm flex items-center gap-1.5'>
                    <FolderSparkleIcon className="size-5" />
                    <span>{item.folderName}</span>
                  </div>
                  <div className='flex flex-wrap gap-[10px]'>
                    {activeTab === 1 && (
                      <ModalTagCreate
                        trigger={
                          <TagItem
                            className='flex items-center gap-1.5'
                          >
                            <TagIcon className='size-4' />
                            <span>{t('assets.material_item.tag_choose_modal.create_tag')}</span>
                          </TagItem>
                        }
                        onConfirm={(name) => handleCreateTag(name, item.id)}
                        loading={createTagLoading}
                      />
                    )}
                    {loading && <SkeletonTagItem />}
                    {!loading && item.tags.map((tag) => (
                      <TagItem
                        key={tag.id}
                        active={selectedIds.includes(tag.id)}
                        onClick={() => handleSelectTag(tag.id)}
                      >
                        {tag.tagName}
                      </TagItem>
                    ))}
                  </div>
                </div>
              ))}
              {/* 空状态处理：当选择页签没有数据时 */}
              {activeTab === 2 && selectedIds.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 text-sm py-10">
                  <FolderClosedIcon className="size-10 mb-2 opacity-20" />
                  <p>{t('assets.material_item.move_modal.no_selected')}</p>
                </div>
              )}
            </div>
            <div className='w-full flex justify-end items-center space-x-3 border-t border-[rgba(234,236,240,1)]  px-[11px]  py-3'>
              <TextButton
                label={moveButtonText || t('assets.material_item.move_modal.move')}
                className={cn(
                  'max-w-max text-sm h-9 bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 !text-[rgba(65,70,81,1)] !font-normal',
                  moveButtonClassName
                )}
                disabled={moveButtonDisabled !== undefined ? moveButtonDisabled : selectedIds.length === 0}
                onClick={handleMove}
              />
              <TextButton
                label={copyButtonText || t('assets.material_item.move_modal.copy')}
                className={cn(
                  'max-w-max text-sm h-9 !font-normal',
                  copyButtonClassName
                )}
                disabled={selectedIds.length === 0}
                onClick={handleCopy}
              />
            </div>
          </div>
        </div>
        <LoadingOverlay loading={saveLoading} />
        {/* 文件夹创建弹窗 */}
        {openFolderCreate && (
          <ModalFolderCreate
            open={openFolderCreate}
            onOpenChange={setOpenFolderCreate}
            onConfirm={handleCreateFolder}
            loading={createFolderLoading}
          />
        )}
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}

const SkeletonTagItem = () => {
  return (
    Array.from({ length: 4 }).map((_, index) => (
      <Skeleton
        key={index}
        className='w-[80px] h-[33px] rounded-[4px]'
      />
    ))
  )
}